'use client';

import { IconCloseFilled } from '@zillow/constellation-icons/react/filled/icon-close-filled';
import { type ElementType, Fragment, forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { useDisabledAnchor } from '../../hooks/use-disabled-anchor';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { Icon } from '../icon/icon';
import { Slot } from '../slot';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { closeButtonRecipe } from './styles/close-button.styles';
import type { CloseButtonPropsInterface } from './types/close-button.types';

export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonPropsInterface>(
  (props: CloseButtonPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      disabled = false,
      className,
      css: cssProp,
      onImpact = false,
      label,
      title = 'Close',
      ...rest
    } = props;

    const styles = css(closeButtonRecipe.raw({ disabled, onImpact }), cssProp);

    const Component: ElementType = asChild ? Slot : 'button';

    const a11yDisabledState = useDisabledState({ disabled, focusable: true });

    // Check if `CloseButton` has been turned into an anchor and needs disabled anchor props
    const childrenModified = useDisabledAnchor({
      disabled,
      asChild,
      children,
      a11yProps: a11yDisabledState,
    });

    const content = useDecoratedSlotContent<'button'>({
      children: childrenModified,
      asChild,
      template: (childContent) => (
        <Fragment>
          <VisuallyHidden>{label ?? title}</VisuallyHidden>
          {childContent}
        </Fragment>
      ),
      fallback: <Icon render={<IconCloseFilled />} />,
      dependencies: [label, title],
    });

    return (
      <Component
        data-c11n-component="CloseButton"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        ref={forwardedRef}
        title={label ?? title}
        type={asChild ? undefined : 'button'}
        {...rest}
        {...a11yDisabledState}
      >
        {content}
      </Component>
    );
  },
);

CloseButton.displayName = 'CloseButton';
